import React, { useMemo, useState } from 'react';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';
import SectionContainer from '../../SectionBuilder/SectionContainer';

/** Labels from the Journal frame when Console has not supplied a filter block. */
const FALLBACK_FILTERS = [
  'All',
  'Wardrobe',
  'Style',
  'Occasions',
  'Women',
  'The Dear Danielle Edit',
];

const textOf = field => (typeof field?.content === 'string' ? field.content : '');

const linesOf = block =>
  [textOf(block?.title), textOf(block?.text), textOf(block?.description)]
    .filter(Boolean)
    .join('\n')
    .split(/\n+/)
    .map(line => line.replace(/^[-*]\s*/, '').replace(/\[(.*)\].*/, '$1').trim())
    .filter(Boolean);

const hasImage = block => Boolean(block?.media?.image || block?.media?.fieldType === 'image');

/**
 * A block with no photograph and several short lines is the filter list.
 * Story cards always carry an image.
 */
const isFilterBlock = block => !hasImage(block) && linesOf(block).length >= 3;

/** Console stores the category in the block title. The story name is the text. */
const categoryOf = block => textOf(block?.title).trim();

/**
 * Journal story grid. Console section id: `journal-card-grid-container`.
 * The first imageless block, when present, is the category list. Every other
 * block is a story card. "All" shows every card; the other tabs keep cards
 * whose copy names that category.
 *
 * @component
 * @param {Object} props Page Builder section props
 * @returns {JSX.Element}
 */
const JournalCardGrid = props => {
  const {
    sectionId = 'journal-card-grid-container',
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    options,
  } = props;

  const fieldOptions = { fieldComponents: options?.fieldComponents };
  const filterBlock = blocks.find(isFilterBlock);
  const cardBlocks = filterBlock ? blocks.filter(block => block !== filterBlock) : blocks;
  const filters = filterBlock
    ? ['All', ...linesOf(filterBlock).filter(label => label.toLowerCase() !== 'all')]
    : FALLBACK_FILTERS;

  const [active, setActive] = useState('All');

  const visibleBlocks = useMemo(() => {
    if (active.toLowerCase() === 'all') {
      return cardBlocks;
    }
    return cardBlocks.filter(
      block => categoryOf(block).toLowerCase() === active.toLowerCase()
    );
  }, [active, cardBlocks]);

  const hasHeader = hasDataInFields([title, description, callToAction], fieldOptions);

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeader ? (
        <header className={defaultClasses?.sectionDetails}>
          <Field data={title} className={defaultClasses?.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses?.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses?.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      <div>
        <nav aria-label="Journal categories">
          {filters.map(label => (
            <button
              key={label}
              type="button"
              aria-pressed={active === label}
              onClick={() => setActive(label)}
            >
              {label}
            </button>
          ))}
        </nav>
        <div>
          <BlockBuilder
            blocks={visibleBlocks.map((block, index) => ({
              ...block,
              blockId: `${sectionId}-card-${index + 1}`,
            }))}
            sectionId={sectionId}
            responsiveImageSizes="(max-width: 767px) 100vw, 600px"
            options={options}
          />
        </div>
      </div>
    </SectionContainer>
  );
};

export default JournalCardGrid;
